import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://hireit-production-ac0f.up.railway.app'

const EXCLUDED_LABELS = {
stripe_connected: 'already connected Stripe',
admin: 'admins',
unsubscribed: 'unsubscribed from emails',
reminded_last_14_days: 'sent this reminder in the last 14 days',
auto_nudge_last_14_days: 'got the automatic Stripe email in the last 14 days',
our_own_address: 'our own email addresses',
no_email: 'no email on their account',
}

// Admin → Growth → "Published a listing": one-off Stripe reminder.
// The backend works out who gets it; this shows the list, a preview, a test
// send to hello@hireitnow.au, and the real send.
export default function StripeReminderModal({ onClose }) {
const [preview, setPreview] = useState(null)
const [busy, setBusy] = useState('')
const [message, setMessage] = useState(null)
// Owners the admin has unticked (by id) — they're skipped on send
const [unticked, setUnticked] = useState(new Set())

const call = async (body) => {
const { data: { session } } = await supabase.auth.getSession()
const res = await fetch(`${BACKEND_URL}/admin/stripe-reminder`, {
method: 'POST',
headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
body: JSON.stringify(body),
})
const json = await res.json().catch(() => ({}))
if (!res.ok) throw new Error(json.error || `Request failed (${res.status})`)
return json
}

const loadPreview = async () => {
setBusy('preview')
try { setPreview(await call({ mode: 'preview' })) }
catch (err) { setMessage({ type: 'error', text: err.message }) }
setBusy('')
}

// eslint-disable-next-line
useEffect(() => { loadPreview() }, [])

const sendTest = async () => {
setBusy('test'); setMessage(null)
try {
const r = await call({ mode: 'test' })
setMessage({ type: 'success', text: `Test sent to ${r.sentTo}. Check your inbox.` })
} catch (err) { setMessage({ type: 'error', text: err.message }) }
setBusy('')
}

const selectedCount = preview ? preview.count - (preview.recipients || []).filter(r => unticked.has(r.id)).length : 0
const toggle = (id) => setUnticked(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })

const sendAll = async () => {
if (!window.confirm(`Send the Stripe reminder to ${selectedCount} ${selectedCount === 1 ? 'owner' : 'owners'} now?`)) return
setBusy('send'); setMessage(null)
try {
const r = await call({ mode: 'send', expectedCount: preview.count, excludeIds: [...unticked] })
setMessage({ type: 'success', text: r.started ? `Sending to ${r.total} ${r.total === 1 ? 'owner' : 'owners'} now (about 1–2 a second). Re-open this window later to see the updated list.` : 'Nobody to send to.' })
setPreview(p => ({ ...p, count: 0, sentNow: true }))
} catch (err) { setMessage({ type: 'error', text: err.message }) }
setBusy('')
}

const excludedRows = preview ? Object.entries(preview.excluded || {}).filter(([, n]) => n > 0) : []

return (
<div className="modal-overlay" onClick={onClose}>
<div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 760, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
<h3 style={{ fontWeight: 700, marginBottom: 4, color: 'var(--navy)' }}>💳 Send Stripe reminder</h3>
<p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>Owners with a live listing who haven't connected Stripe yet. Each email is sent individually from hello@hireitnow.au.</p>

{!preview && busy === 'preview' && <div className="spinner-wrap"><div className="spinner" /></div>}

{preview && (
<>
<div style={{ background: 'var(--primary-pale)', borderRadius: 12, padding: '12px 14px', marginBottom: 12 }}>
<div style={{ fontSize: 22, fontWeight: 700, color: 'var(--primary)' }}>{preview.sentNow ? 'Sending…' : `${selectedCount} ${selectedCount === 1 ? 'owner' : 'owners'}`}</div>
<div style={{ fontSize: 12, color: 'var(--text-muted)' }}>will get this email{selectedCount < preview.count && !preview.sentNow ? ` (${preview.count - selectedCount} unticked)` : ''}</div>
{excludedRows.length > 0 && (
<div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
Left out: {excludedRows.map(([k, n]) => `${n} ${EXCLUDED_LABELS[k] || k}`).join(' · ')}
</div>
)}
</div>

{preview.recipients?.length > 0 && !preview.sentNow && (
<div style={{ marginBottom: 12, fontSize: 13 }}>
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
<strong style={{ color: 'var(--navy)' }}>Who gets it — untick anyone to leave them out</strong>
<span style={{ display: 'flex', gap: 10 }}>
<button type="button" onClick={() => setUnticked(new Set())} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: 12, padding: 0 }}>Tick all</button>
<button type="button" onClick={() => setUnticked(new Set(preview.recipients.map(r => r.id)))} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: 12, padding: 0 }}>Untick all</button>
</span>
</div>
<div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
{preview.recipients.map((r, i) => (
<label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderTop: i ? '1px solid var(--border)' : 'none', cursor: 'pointer', opacity: unticked.has(r.id) ? 0.5 : 1 }}>
<input type="checkbox" checked={!unticked.has(r.id)} onChange={() => toggle(r.id)} />
<span><strong>{r.firstName === 'there' ? '(no first name — "Hi there")' : r.firstName}</strong> <span style={{ color: 'var(--text-muted)' }}>— {r.listingTitle}</span></span>
</label>
))}
</div>
</div>
)}

<div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Subject</div>
<div style={{ fontWeight: 600, marginBottom: 10 }}>{preview.subject}</div>
<div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Preview {preview.recipients?.[0] ? `(as ${preview.recipients[0].firstName} would see it)` : '(sample)'}</div>
<iframe title="Email preview" srcDoc={preview.html} sandbox="" style={{ width: '100%', height: 520, border: '1px solid var(--border)', borderRadius: 8, background: 'white', marginBottom: 12 }} />
</>
)}

{message && <div className={`alert ${message.type === 'error' ? 'alert-error' : 'alert-success'}`} style={{ marginBottom: 12, fontSize: 13 }}>{message.text}</div>}

<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
<button className="btn btn-ghost" onClick={onClose} disabled={busy === 'send'}>Close</button>
<button className="btn btn-ghost" onClick={sendTest} disabled={!preview || !!busy}>{busy === 'test' ? 'Sending test…' : 'Send test to me'}</button>
<button className="btn btn-gradient" onClick={sendAll} disabled={!preview || !!busy || !selectedCount || preview.running || preview.sentNow}>
{busy === 'send' ? 'Starting…' : preview?.running ? 'A send is already running' : `Send to ${selectedCount} ${selectedCount === 1 ? 'owner' : 'owners'}`}
</button>
</div>
</div>
</div>
)
}
